import { ReactNode, useCallback, useEffect, useRef } from "react";
import { clsx } from "clsx";

import { useTheme } from "@/shared/lib/hooks/useTheme";
import { Icon } from "@/shared/ui/Icon";
import { Spinner } from "@/shared/ui/Spinner";

import closeIcon from "@/shared/assets/xicon.svg";

import { useDevice } from "../../lib/hooks/useDevice";
import { useModal } from "../../lib/hooks/useModal";
import { Portal } from "../Portal";

import cls from "./Modal.module.scss";

interface ModalProps {
  contentId?: string;
  className?: string;
  contentClassName?: string;
  children?: ReactNode | ReactNode[];
  isOpen?: boolean;
  isBusy?: boolean;
  onClose?: () => void;
  onResize?: (width: number, height: number) => void;
}

export const Modal = ({
  contentId,
  className,
  contentClassName,
  children,
  isOpen,
  isBusy,
  onClose,
  onResize,
}: ModalProps) => {
  const { isMobile } = useDevice();
  const { theme } = useTheme();

  const modalRef = useRef<HTMLDivElement>(null);

  const onResizeHandler = useCallback(() => {
    if (onResize) {
      onResize(modalRef.current?.offsetWidth ?? 0, modalRef.current?.offsetHeight ?? 0);
    }
  }, [modalRef, onResize]);

  const { isMounted, isClosing, closeModal } = useModal({
    isOpen,
    onClose,
    animationDelay: 200,
  });

  useEffect(() => {
    onResizeHandler();

    window.addEventListener("resize", onResizeHandler);

    return () => {
      window.removeEventListener("resize", onResizeHandler);
    };
  }, [isMounted, onResizeHandler]);

  const mods = {
    [cls.isOpen]: isOpen,
    [cls.isClosing]: isClosing,
    [cls.isMobile]: isMobile,
  };

  return (
    <Portal>
      <div className={clsx(cls.Modal, mods, theme)}>
        <div className={cls.Overlay} onClick={closeModal} />
        <div className={clsx(cls.WrapperContent, className)}>
          {isMounted && (
            <div ref={modalRef} id={contentId} className={clsx(cls.Content, contentClassName, isBusy && cls.isBusy)}>
              {isBusy && (
                <div className={cls.LoadingSpinner}>
                  <Spinner />
                </div>
              )}
              <div className={cls.Text}>{children}</div>
            </div>
          )}
          {!isClosing && <Icon Svg={closeIcon} className={cls.CloseButton} onClick={closeModal} />}
        </div>
      </div>
    </Portal>
  );
};
